import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoIcon from '@mui/icons-material/Info';
import {
    Box,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Typography,
} from '@mui/material';
import React, {useEffect, useState} from 'react';
import {useMutation, useQuery, useQueryClient} from 'react-query';
import {useDispatch} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import {toast} from 'react-toastify';
import {v4 as uuid} from 'uuid';
import {useAppBar} from '../../../../components/appBar/appBarContext';
import CustomisedButton from '../../../../components/button/button';
import ConfirmDeleteModal from '../../../../components/modal/confirmDeleteModal';
import {INQUIRY_REQUIRED_OTP} from '../../../../config/cacheKey';
import {ApiResultModel} from '../../../../models/common.model';
import {
    ITotalInquiryResponse,
    TotalInquiryRequest,
} from '../../../../models/inquiry.model';
import {changeRouteType} from '../../../../redux/handleBackBtn/handleBackBtnSlice';
import {TotalInquiry} from '../../../../services/inquiry.service';
import {
    CancelLoan,
    RetryLoanInquiries,
} from '../../../../services/loan.service';
import {IInquiry} from '../IInquriry';
import './inquiryResult.css';
import InquiryResultSkeleton from './inquiryResultSkeleton';

const InquiryResult = ({referenceKey, setInnerState, setStep}: IInquiry) => {
    const navigate = useNavigate();
    const [openVerifyModal, setOpenVerifyModal] = useState(false);
    const [confirm, setConfirm] = useState(false);
    const {setHeader} = useAppBar();
    const dispatch = useDispatch();
    useEffect(() => {
        setHeader('نتیجه استعلام‌ها', 'inquiry');
        dispatch(changeRouteType({routeType: 'infoDetail'}));
    }, []);
    const handleContinue = () => {
        setStep(uuid());
    };
    const queryClient = useQueryClient();
    const {
        data: inquiryResponse,
        refetch: refetchInquiry,
        isLoading,
    } = useQuery(
        ['totalInquiry', referenceKey],
        () => TotalInquiry({referenceKey} as TotalInquiryRequest),
        {
            enabled: Boolean(referenceKey),
            staleTime: 1000,
            refetchOnWindowFocus: false,
            refetchInterval: (result) =>
                result?.data?.state === 'InProgress' ? 60 * 1000 : false,
            onSuccess: (result: ApiResultModel<ITotalInquiryResponse>) => {
                if (result.isSuccess && result.data) {
                    if (result.data.requiredOtpValidation) {
                        queryClient.setQueryData(
                            INQUIRY_REQUIRED_OTP,
                            result.data.requiredOtpValidation
                        );
                        setInnerState!('otp');
                    }
                }
            },
        }
    );
    const inquiryResult = inquiryResponse?.isSuccess
        ? inquiryResponse.data
        : undefined;
    const data = inquiryResult?.inquiries;
    const retryInquiriesMutation = useMutation(RetryLoanInquiries, {
        onSuccess: async (result) => {
            if (result.isSuccess) {
                await refetchInquiry();
            }
        },
    });
    const handleRetry = () => {
        if (referenceKey) {
            retryInquiriesMutation.mutate({referenceKey});
        }
    };
    const cancelLoanMutation = useMutation(CancelLoan, {
        onSuccess: (data: boolean) => {
            if (data == true) {
                toast.success('حذف تسهیلات با موفقیت انجام شد.');
                navigate('/loans');
            }
        },
    });

    const handleCancel = () => {
        cancelLoanMutation.mutate({referenceKey: referenceKey!});
    };
    useEffect(() => {
        if (confirm === true) {
            handleCancel();
            setConfirm(false);
        }
    }, [confirm]);

    const handleCancelLoan = () => {
        setOpenVerifyModal(true);
    };

    if (isLoading) {
        return <InquiryResultSkeleton />;
    }
    return (
        <div className="container-height-drawer">
            <Box className="content-container">
                <List className="drawer-content">
                    <Typography sx={{marginBottom: '10px'}}>
                        ادامه فرآیند درخواست تسهیلات، نیازمند دریافت استعلام‌های
                        زیر می‌باشد.
                    </Typography>
                    {data?.map((item, index) => (
                        <ListItem
                            key={index}
                            sx={{
                                padding: '4px !important',
                                alignItems: 'flex-start',
                                position: 'relative',
                                paddingY: '16px',
                                '&:before': {
                                    content: '""',
                                    position: 'absolute',
                                    right: '17px',
                                    top: '20%',
                                    bottom:
                                        index === data.length - 1
                                            ? 'auto'
                                            : '-5%',
                                    width: '1px',
                                    backgroundColor:
                                        index === data.length - 1
                                            ? 'transparent'
                                            : '#ccc',
                                },
                            }}
                        >
                            <ListItemIcon sx={{minWidth: '40px', zIndex: '5'}}>
                                {item.state == 'Approved' ? (
                                    <CheckCircleIcon className="text-[#0158CC] bg-[var(--bg-color)] rounded-full iconSize" />
                                ) : item.state == 'Rejected' ? (
                                    <CancelIcon className="primaryErrorColorLight iconSize" />
                                ) : [
                                      'WaitForInquiry',
                                      'WaitForOtpSending',
                                      'WaitForOtpSubmitting',
                                      'InProgress',
                                  ].includes(item.state) ? (
                                    <span className="inquiry-result-icon-background">
                                        <img
                                            src="/images/rotating-arrows-circle.svg"
                                            alt=""
                                            className="inquiry-result-rotating-icon"
                                        />
                                    </span>
                                ) : (
                                    <InfoIcon className="iconSize" />
                                )}
                            </ListItemIcon>
                            <ListItemText
                                sx={{marginTop: '2px'}}
                                primary={
                                    <Typography
                                        variant="body1"
                                        textAlign="right"
                                        fontWeight="bold"
                                        className="text-[var(--primary-text-color)]"
                                    >
                                        {item.inquiryTypeTitle} &nbsp;
                                    </Typography>
                                }
                                secondary={
                                    <Typography
                                        variant="body2"
                                        className="primaryColorLight-100"
                                        textAlign="right"
                                    >
                                        {item.inquiryInfo}&nbsp;
                                    </Typography>
                                }
                            />
                        </ListItem>
                    ))}
                </List>
                {inquiryResult?.allowContinueRequest && (
                    <Box className="py-1">
                        <CustomisedButton
                            title="ادامه"
                            disable={
                                retryInquiriesMutation.isLoading ||
                                cancelLoanMutation.isLoading
                            }
                            onClick={handleContinue}
                            type="button"
                        />
                    </Box>
                )}
                {inquiryResult?.allowRetryInquiries && (
                    <Box className="py-1">
                        <CustomisedButton
                            title="تلاش مجدد"
                            disable={
                                retryInquiriesMutation.isLoading ||
                                cancelLoanMutation.isLoading
                            }
                            onClick={handleRetry}
                            type="button"
                        />
                    </Box>
                )}
                {inquiryResult?.allowCancelRequest && (
                    <Box className="py-1">
                        <CustomisedButton
                            title="انصراف از تسهیلات"
                            specializedColor="#FF617C !important"
                            disable={
                                retryInquiriesMutation.isLoading ||
                                cancelLoanMutation.isLoading
                            }
                            onClick={handleCancelLoan}
                            type="button"
                        />
                    </Box>
                )}
            </Box>
            <ConfirmDeleteModal
                text={'آیا از حذف تسهیلات مورد نظر اطمینان داید؟'}
                buttonTitle={'بله'}
                delButton={true}
                openModal={openVerifyModal}
                setOpenVerifyModal={setOpenVerifyModal}
                setConfirm={setConfirm}
                deleteTitle={'خیر'}
            />
        </div>
    );
};

export default InquiryResult;
